import { useId, useState } from 'react';
import { Button, Label, SearchableSelect } from '@eo/shared';

import { HoursStepper } from './HoursStepper';
import type { ShareFormProps } from './types';

export function ShareForm({ people, initial, onSave }: ShareFormProps) {
  const [userId, setUserId] = useState(initial?.userId ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [fixedHours, setFixedHours] = useState(initial?.fixedHours ?? 0);
  const [variableHours, setVariableHours] = useState(initial?.variableHours ?? 0);
  const [saving, setSaving] = useState(false);
  const id = useId();

  const ready = userId !== '' && !saving;

  const submit = async () => {
    if (!ready) return;
    setSaving(true);
    try {
      await onSave({ userId, fixedHours, variableHours });
    } catch {
      // The failure was already announced; the form stays open to retry.
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      {initial ? (
        <p className="text-sm text-text-primary">{initial.name}</p>
      ) : (
        <SearchableSelect
          value={userId}
          selectedLabel={name || undefined}
          ariaLabel="Person"
          placeholder="Person…"
          emptyLabel="No one matches"
          options={people.options}
          loading={people.loading}
          hasMore={people.hasMore}
          onSearch={people.onSearch}
          onLoadMore={people.onLoadMore}
          onChange={(value) => {
            setUserId(value);
            setName(people.options.find((o) => o.value === value)?.label ?? '');
          }}
        />
      )}
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${id}-fixed`}>Fixed hours</Label>
          <HoursStepper
            id={`${id}-fixed`}
            label="Fixed hours"
            value={fixedHours}
            min={0}
            onCommit={setFixedHours}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${id}-variable`}>Variable hours</Label>
          <HoursStepper
            id={`${id}-variable`}
            label="Variable hours"
            value={variableHours}
            min={0}
            onCommit={setVariableHours}
          />
        </div>
      </div>
      <Button type="submit" size="sm" disabled={!ready}>
        {saving ? 'Saving…' : 'Save'}
      </Button>
    </form>
  );
}
